import React from 'react';
import { Table, Typography } from 'antd';
import { ErrorHandler } from '@common/components';
import { gql, useQuery, useNavigate } from '@common/hooks';
import { Colors } from 'works/legacy/lib/style-helper';
import { StaffingRequestType } from '@applicant-flow/types';

const { Title } = Typography;

const PENDING_APPROVAL_CHAINS_QUERY = gql`
  query ApprovalChainsPendingActionListQuery {
    approvalChainsPending {
      id
      approvable {
        ... on StaffingRequest {
          id
          code
          name
          state
          stateChangedAt
          hiringLeader {
            id
            name
          }
          canOpenUp {
            value
          }
          canHold {
            value
          }
          canCancel {
            value
          }
          canUpdate {
            value
          }

          role {
            id
            abbreviation
          }

          staffingRequestType
          shiftType
          shiftsPerWeek
          hoursPerShift

          startDate
          length

          staffingRequestPositionsCount
          openStaffingRequestPositionsCount
          acceptedStaffingRequestApplicationsCount
          submittedStaffingRequestApplicationsCount
          inReviewStaffingRequestApplicationsCount
          offeredOffersStaffingRequestApplicationsCount

          organizationUnit {
            id
            name
            clinicalUnit {
              id
              displayName
            }

            department {
              id
              name

              organizationBusiness {
                id
                name
                city
                stateCode
              }
            }
          }
          __typename
        }
        ... on ExtensionOffer {
          id
          __typename
        }
      }
    }
  }
`;

type listColumn = {
  title: string;
  dataIndex: string;
  key: string;
  sorter?: boolean;
  render?: (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: any,
    staffingRequest: StaffingRequestType
  ) => React.JSX.Element | string;
};

function getFlatColumns(columns: listColumn[]) {
  return columns.map((col: listColumn) => ({
    title: col.title,
    dataIndex: col.dataIndex,
    key: col.key,
    sorter: col.sorter,
    render: col.render,
  }));
}

export function StaffingRequestsApprovalsPendingList({
  columns,
}: {
  columns: listColumn[];
}) {
  const navigate = useNavigate();
  const { loading, data, error } = useQuery(PENDING_APPROVAL_CHAINS_QUERY);
  const flatColumns = getFlatColumns(columns);

  if (error) {
    return (
      <ErrorHandler
        error={error}
        log="StaffingRequestsApprovalsPendingList"
        messageType="contextual"
      />
    );
  }

  if (
    loading ||
    !data?.approvalChainsPending ||
    !data.approvalChainsPending.length
  ) {
    return <></>;
  }

  const dataSource = data.approvalChainsPending
    .map(
      (approvalChainPending: {
        approvable: {
          __typename: 'ExtensionOffer' | 'StaffingRequest';
        };
      }) => approvalChainPending.approvable
    )
    .filter(
      (ap: { __typename: 'ExtensionOffer' | 'StaffingRequest' }) =>
        ap.__typename === 'StaffingRequest'
    );

  if (dataSource.length === 0) {
    return <></>;
  }

  return (
    <>
      <Title level={2}>Staffing Requests Pending Your Approval</Title>
      <Table
        rowKey="id"
        className="staffing-requests-pending-approval-list"
        scroll={{ x: 'scroll' }}
        columns={flatColumns}
        dataSource={dataSource}
        onRow={(record) => {
          return {
            onClick: () => {
              navigate(`/works/staffing_requests/${record.id}`);
            },
          };
        }}
        pagination={false}
      />
      <style jsx>{`
        :global(.staffing-requests-pending-approval-list) {
          margin-bottom: 40px;
        }
        :global(.staffing-requests-pending-approval-list tbody tr) {
          cursor: pointer;
          background-color: ${Colors.harvestGoldHyperLight};
          box-shadow: inset 4px 0px 0px ${Colors.harvestGold};
        }
      `}</style>
    </>
  );
}
